/**
 * 独自テンプレート / 配布テンプレートの新規作成 / 編集モーダル (1 つに統合)。
 *
 * 「任意テキスト」画面は 2 種類のテンプレートを扱う (DynamoDB は別テーブルのまま)。
 *   - `kind='free'`  (独自テンプレート / コピー型): 選択時に放送文面へ「コピー」される。
 *     編集・削除しても登録済みの放送地点は変わらない。
 *   - `kind='synced'` (配布テンプレート / 参照型): 放送地点が「参照」する。編集は参照しているすべての
 *     放送地点の放送内容に約 1 分以内に反映される (⚠️ コピーではない)。
 * 新規作成では種別ラジオ (独自 / 配布、既定は独自) で選ぶ。編集中は種別を変えられない (保存先テーブルが違うため)。
 *
 * タイトルと備考は管理用の項目で、放送内容には影響しない。タイトルは新規だけ必須。
 * 編集では、既存のタイトルを空にすることだけを拒否し、タイトルを持たない既存行は空のまま保存できる。
 *
 * SSML 辞書マスタの編集モーダル (`SsmlDictionaryFormModal`) と同じ骨格
 * (`.modal-backdrop` > `.modal` > `.modal-header` / `.modal-body` / `.modal-footer`) で表示する。
 *
 * - 新規 (`mode='create'`) は POST、編集 (`mode='edit'`) は PUT で保存する。
 *   編集中は線区 (`route_id`) がキーなので変更できない (変えたいときは削除して登録し直す)。
 * - 入力の妥当性検証はサーバ側が正源。400 等のメッセージは日本語に変換して表示し、
 *   モーダルは開いたままにして入力内容を失わないようにする。
 *   - 原因の入力欄が特定できるもの: その欄にブラウザ標準の入力検証の吹き出し
 *     (`setCustomValidity` + `reportValidity`) + 赤枠 (`.is-invalid`) + 欄の直下の文で表示する。
 *   - 特定できないもの (認証切れ / サーバエラー / 通信断 など): ヘッダー直下のバナーに表示する。
 * - 保存に成功したら `onSaved` を呼ぶ。閉じる / 一覧の再取得は親の責務。
 * - 背景クリック・ESC では閉じない (誤操作で入力を失わないため)。
 * - 本文は保存前でも「▶ 試聴」で聴ける (SSML 辞書マスタの編集モーダルと同じ操作感)。
 *   既存の `POST /audio-preview` を使い、本番の放送生成と同じく SSML 辞書を適用して合成する
 *   (聴いた音 = 本番の音)。本文を編集すると、食い違う古い音声は破棄する。
 */
import { Spinner } from './Spinner';
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

import { base64ToAudioUrl, type BroadcastPointsClient } from '../api/broadcastPoints';
import type { FreeTextTemplatesClient } from '../api/freeTextTemplates';
import type { SyncedTextsClient } from '../api/syncedTexts';
import type { FreeTextTemplate, FreeTextTemplateSaveResult } from '../api/types';
import {
  FREE_TEXT_TEMPLATE_ALL_ROUTES,
  FREE_TEXT_TEMPLATE_ALL_ROUTES_LABEL,
  MAX_FREE_TEXT_TEMPLATE_TEXT_LENGTH,
  MAX_TEMPLATE_NOTE_LENGTH,
  MAX_TEMPLATE_TITLE_LENGTH,
} from '../data/freeTextTemplates';
import type { RouteMaster } from '../data/routes';

import {
  FREE_TEXT_TEMPLATE_REQUIRED_MESSAGES,
  describeApiError,
  formatNoteCounter,
  formatTextCounter,
  formatTitleCounter,
  type FreeTextTemplateErrorField,
} from './freeTextTemplateMessages';
import { StatusBanner } from './StatusBanner';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { judgeRemoteChange } from '../utils/remoteChange';

/** テンプレートの種別 (free = 独自テンプレート / synced = 配布テンプレート) */
export type FreeTextKind = 'free' | 'synced';

/** 種別ラジオの選択肢 (既定の独自を先頭にする) */
const KIND_OPTIONS: ReadonlyArray<{ kind: FreeTextKind; label: string; description: string }> = [
  {
    kind: 'free',
    label: '独自テンプレート',
    description:
      '使うときに文面がコピーされます。ここを直したり消したりしても、登録済みの放送地点は変わりません。',
  },
  {
    kind: 'synced',
    label: '配布テンプレート',
    description:
      '放送地点は文面をコピーせず、ここの文面を参照します。ここを直すと、使っているすべての放送地点の放送が約 1 分以内に自動で変わります。',
  },
];

/** モーダルが扱う保存先 API の共通インターフェース (両 Client は同じメソッド名・型) */
type TextApiClient = FreeTextTemplatesClient | SyncedTextsClient;

/** 入力フォームの状態 */
interface FormState {
  routeId: string;
  title: string;
  text: string;
  note: string;
}

/** 欄ごとのエラー文 (キーが無い欄はエラーなし) */
type FieldErrors = Partial<Record<FreeTextTemplateErrorField, string>>;

/** エラー欄の走査順 (画面の上から下) */
const FIELD_ORDER: readonly FreeTextTemplateErrorField[] = ['route', 'title', 'text', 'note'];

/** 入力検証 API (`setCustomValidity` など) を持つ入力要素 */
type ValidatableElement = HTMLTextAreaElement | HTMLSelectElement | HTMLInputElement;

/** 入力欄の直下に出す、原因欄のエラー文 (赤枠 `.is-invalid` と対)。 */
function FieldError(props: { id: string; message: string | undefined }): React.JSX.Element | null {
  if (!props.message) return null;
  return (
    <span id={props.id} className="field-error" data-testid={props.id}>
      {props.message}
    </span>
  );
}

export interface FreeTextTemplateFormModalProps {
  /** 新規 (create) / 編集 (edit) */
  mode: 'create' | 'edit';
  /** 編集対象 (mode='edit' のとき必須)。新規のときは null */
  template: FreeTextTemplate | null;
  /**
   * 編集対象の種別 (mode='edit' のときこの種別で固定)。新規のときは初期選択として使う (既定は 'free')。
   */
  initialKind?: FreeTextKind;
  /** 新規作成時に選んでおく線区 (一覧の絞り込み中の線区。全線区共通も可) */
  defaultRouteId: string;
  /** 線区マスタ (線区の選択肢) */
  routes: readonly RouteMaster[];
  /** 任意テキスト (`/free-text-templates`) の API クライアント */
  apiClient: FreeTextTemplatesClient;
  /** 共有テキスト (`/synced-texts`) の API クライアント */
  syncedClient: SyncedTextsClient;
  /** 試聴用 (`POST /audio-preview`)。放送地点 API のクライアントを流用する */
  previewClient: BroadcastPointsClient;
  /** 保存成功時に呼ばれる (保存した種別と、サーバの warnings を含む結果を渡す) */
  onSaved: (result: FreeTextTemplateSaveResult, kind: FreeTextKind) => void;
  /**
   * 編集中に他者が削除していたとき、「他者によって削除されています」のお知らせを閉じた後に呼ばれる
   * (画面側でモーダルを閉じて一覧を取り直す)。
   */
  onDeletedByOthers: () => void;
  /** 閉じる (キャンセル / ×) */
  onClose: () => void;
}

export function FreeTextTemplateFormModal(
  props: FreeTextTemplateFormModalProps,
): React.JSX.Element {
  const {
    mode,
    template,
    initialKind,
    defaultRouteId,
    routes,
    apiClient,
    syncedClient,
    previewClient,
    onSaved,
    onDeletedByOthers,
    onClose,
  } = props;
  const isEditing = mode === 'edit';
  /** 保存先の種別。編集中は変えられない */
  const [kind, setKind] = useState<FreeTextKind>(initialKind ?? 'free');
  const kindClient: TextApiClient = kind === 'synced' ? syncedClient : apiClient;

  const [form, setForm] = useState<FormState>(() => ({
    routeId: template?.route_id ?? defaultRouteId,
    title: template?.title ?? '',
    text: template?.text ?? '',
    note: template?.note ?? '',
  }));
  /** 開いたときの入力内容 (編集時に「何か変更したか」を判定する基準 / Issue #765) */
  const [initialForm] = useState<FormState>(() => ({
    routeId: template?.route_id ?? defaultRouteId,
    title: template?.title ?? '',
    text: template?.text ?? '',
    note: template?.note ?? '',
  }));
  /** 開いたときの更新日時 (LastUpdate)。保存時に他者の更新 / 削除を判定する基準 (Issue #765) */
  const [baselineUpdatedAt] = useState<string | null>(() => template?.updated_at ?? null);
  const [saving, setSaving] = useState(false);
  const { confirmDiscard, guardedUpdate, confirmDialog } = useConfirmDialog();
  /** 開いたときから入力内容を変更しているか */
  const dirty =
    form.routeId !== initialForm.routeId ||
    form.title.trim() !== initialForm.title.trim() ||
    form.text.trim() !== initialForm.text.trim() ||
    form.note.trim() !== initialForm.note.trim();
  /** 原因の欄が特定できないエラー (ヘッダー直下のバナーに出す) */
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** 原因の欄が特定できたエラー (該当欄に出す) */
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  /** 破棄すべき試聴 URL (object URL の解放用) */
  const previewUrlRef = useRef<string | null>(null);
  /** 各入力欄の DOM (ブラウザ標準の入力検証の吹き出し / フォーカス用) */
  const fieldRefs = useRef<Partial<Record<FreeTextTemplateErrorField, ValidatableElement | null>>>(
    {},
  );
  /** バナーの DOM (表示時にスクロールして見える位置に持ってくる) */
  const bannerRef = useRef<HTMLDivElement | null>(null);

  /** アンマウント時に試聴の object URL を解放する。 */
  useEffect(() => {
    return () => {
      const url = previewUrlRef.current;
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  /** 試聴音声を破棄する (本文が変わると古い音声は誤解のもとになる)。 */
  const clearPreview = useCallback((): void => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setPreviewError(null);
  }, []);

  const updateForm = useCallback(
    (patch: Partial<FormState>): void => {
      setForm((prev) => ({ ...prev, ...patch }));
      // 入力し直した欄のエラーは解除する (再保存で再検証される)。
      setFieldErrors((prev) => {
        if (Object.keys(prev).length === 0) return prev;
        const next = { ...prev };
        if ('routeId' in patch) delete next.route;
        if ('title' in patch) delete next.title;
        if ('text' in patch) delete next.text;
        if ('note' in patch) delete next.note;
        return next;
      });
      // 本文を変えたら、以前の試聴音声は今の入力と食い違うので破棄する。
      if ('text' in patch) clearPreview();
    },
    [clearPreview],
  );

  /**
   * 欄ごとのエラーを、ブラウザ標準の入力検証 (`setCustomValidity`) に反映する。
   * エラーがあれば先頭の欄にフォーカスし、標準の吹き出しを出す。
   */
  useEffect(() => {
    let first: ValidatableElement | null = null;
    for (const field of FIELD_ORDER) {
      const el = fieldRefs.current[field];
      if (!el) continue;
      const message = fieldErrors[field];
      el.setCustomValidity(message ?? '');
      if (message && first === null) first = el;
    }
    if (first) {
      first.focus();
      first.reportValidity();
    }
  }, [fieldErrors]);

  /** バナーが出たら、見える位置までスクロールする。 */
  useEffect(() => {
    const el = bannerRef.current;
    if (errorMessage && el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [errorMessage]);

  /** 実際に保存する (新規 = POST / 編集 = PUT)。入力チェックと他者の更新 / 削除の確認は通過済みの前提。 */
  const executeSave = useCallback(async (): Promise<void> => {
    setSaving(true);
    setErrorMessage(null);
    setFieldErrors({});
    try {
      const title = form.title.trim();
      const note = form.note.trim();
      const result =
        isEditing && template
          ? await kindClient.update(template.route_id, template.template_id, {
              text: form.text,
              // タイトルを持たない既存行を空のまま保存するときは送らない (送るとサーバは変更とみなす)。
              ...(title !== '' && { title }),
              // 備考は空文字を送って削除できる。元も空なら送らない。
              ...((note !== '' || (template.note ?? '') !== '') && { note }),
            })
          : await kindClient.create({
              routeId: form.routeId,
              title,
              text: form.text,
              ...(note !== '' && { note }),
            });
      onSaved(result, kind);
    } catch (err) {
      const info = describeApiError(err);
      if (info.field) {
        setFieldErrors({ [info.field]: info.message });
      } else {
        setErrorMessage(info.message);
      }
      setSaving(false);
    }
  }, [form, isEditing, kind, kindClient, onSaved, template]);

  /**
   * 登録 / 更新ボタン: 入力チェック → (編集の場合) 他者の更新 / 削除の確認 → 保存。
   * 新規登録に「実行してよろしいですか」は出さない (Issue #765)。自分が何も変更していなければ
   * 他者の更新 / 削除の確認も不要 (`hasChanges: false`)。
   */
  const handleSave = useCallback((): void => {
    // ブラウザ標準の `required` は空白だけの入力を通すので、ここでも同じ文言で止める。
    const missing: FieldErrors = {};
    if (form.routeId.trim() === '') missing.route = FREE_TEXT_TEMPLATE_REQUIRED_MESSAGES.route;
    // タイトルは新規だけ必須。編集では、既存のタイトルを空にするときだけ止める。
    if (form.title.trim() === '' && (!isEditing || (template?.title ?? '') !== '')) {
      missing.title = FREE_TEXT_TEMPLATE_REQUIRED_MESSAGES.title;
    }
    if (form.text.trim() === '') missing.text = FREE_TEXT_TEMPLATE_REQUIRED_MESSAGES.text;
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      return;
    }
    if (!isEditing || !template) {
      void executeSave();
      return;
    }
    void guardedUpdate({
      hasChanges: dirty,
      fetchRemoteState: async () => {
        const res = await kindClient.list(template.route_id);
        return judgeRemoteChange(
          baselineUpdatedAt,
          res.items.find((t) => t.template_id === template.template_id),
        );
      },
      execute: () => void executeSave(),
      // 他者に削除されていたら保存できない。お知らせを閉じたら、画面側でモーダルを閉じて一覧を更新する。
      onDeleted: onDeletedByOthers,
    });
  }, [
    kindClient,
    baselineUpdatedAt,
    dirty,
    executeSave,
    form,
    guardedUpdate,
    isEditing,
    onDeletedByOthers,
    template,
  ]);

  /**
   * 閉じる (キャンセル / ×): 登録 / 更新せずに閉じるので破棄の確認を挟む (Issue #765)。
   * 新規は常に確認する。編集は、開いたときから何か変更したときだけ確認する。
   */
  const handleClose = useCallback((): void => {
    if (!isEditing || dirty) {
      confirmDiscard(onClose);
      return;
    }
    onClose();
  }, [confirmDiscard, dirty, isEditing, onClose]);

  const kindLabel = kind === 'synced' ? '配布テンプレート' : '独自テンプレート';
  const modalTitle = isEditing ? `${kindLabel}を編集` : 'テンプレートを新規作成';

  /**
   * 入力中の本文を試聴する (保存はしない)。
   * 辞書は適用する: 本番の放送生成は任意テキストにも SSML 辞書を適用するため、
   * 「聴いた音 = 本番の音」になる。言語・話者は指定しない (日本語・サーバ既定話者)。
   */
  const handlePreview = useCallback(async (): Promise<void> => {
    const text = form.text.trim();
    if (text === '') {
      setPreviewError('試聴するにはテキストを入力してください');
      return;
    }
    clearPreview();
    setPreviewing(true);
    try {
      const res = await previewClient.previewAudio({ text, isSsml: false });
      const url = base64ToAudioUrl(res.content_base64, res.content_type);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (err) {
      setPreviewError(describeApiError(err).message);
    } finally {
      setPreviewing(false);
    }
  }, [clearPreview, form.text, previewClient]);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={modalTitle}
      data-testid="free-text-template-modal"
      // 背景クリック / ESC では閉じない (onClick 未設定)
    >
      <form
        className="modal modal--single-column"
        style={{ maxWidth: 640 }}
        aria-label={isEditing ? `${kindLabel}の編集` : 'テンプレートの登録'}
        data-testid="free-text-template-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!saving) handleSave();
        }}
      >
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 15 }} data-testid="free-text-template-form-title">
            {modalTitle}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            aria-label="閉じる"
            data-testid="free-text-template-modal-close"
          >
            ×
          </button>
        </div>

        <div className="modal-body modal-body--single-column">
          <div className="form-pane">
            {errorMessage ? (
              <div ref={bannerRef}>
                <StatusBanner
                  variant="error"
                  onDismiss={() => setErrorMessage(null)}
                  data-testid="free-text-template-form-error"
                >
                  {errorMessage}
                </StatusBanner>
              </div>
            ) : null}

            <p className="form-required-hint">
              <span className="required-mark">必須</span> がついた項目は必ず入力してください
            </p>

            {isEditing ? (
              <p className="form-kind-fixed" data-testid="free-text-template-kind-fixed">
                種別: <strong>{kindLabel}</strong>
                (編集中は変更できません)
              </p>
            ) : (
              <fieldset
                className="free-text-kind-switch"
                style={{ border: 'none', padding: 0, margin: 0 }}
              >
                <legend style={{ padding: 0 }}>
                  <span className="required">種別</span>
                </legend>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {KIND_OPTIONS.map((option) => (
                    <label
                      key={option.kind}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <input
                        type="radio"
                        name="free-text-template-kind"
                        value={option.kind}
                        checked={kind === option.kind}
                        disabled={saving}
                        data-testid={`free-text-template-kind-${option.kind}`}
                        onChange={() => {
                          setKind(option.kind);
                          // 種別を変えるとサーバが返した古い入力エラーは意味を失うので消す。
                          setFieldErrors({});
                          setErrorMessage(null);
                        }}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
                <small data-testid="free-text-template-kind-description">
                  {KIND_OPTIONS.find((o) => o.kind === kind)?.description}
                </small>
              </fieldset>
            )}

            <label>
              <span className="required">線区</span>
              <select
                data-testid="free-text-template-route"
                value={form.routeId}
                disabled={isEditing || saving}
                ref={(el) => {
                  fieldRefs.current.route = el;
                }}
                className={fieldErrors.route ? 'is-invalid' : ''}
                aria-invalid={fieldErrors.route ? true : undefined}
                aria-describedby={fieldErrors.route ? 'free-text-template-route-error' : undefined}
                onChange={(e) => updateForm({ routeId: e.target.value })}
              >
                <option value={FREE_TEXT_TEMPLATE_ALL_ROUTES}>
                  {FREE_TEXT_TEMPLATE_ALL_ROUTES_LABEL}
                </option>
                {routes.map((r) => (
                  <option key={r.route_id} value={r.route_id}>
                    {r.route_name} ({r.route_id})
                  </option>
                ))}
              </select>
              <FieldError id="free-text-template-route-error" message={fieldErrors.route} />
              <small>
                「全線区共通」は、どの線区の放送地点でも選択肢に出ます。
                {isEditing
                  ? '編集中は変更できません。変えたい場合は削除して登録し直してください。'
                  : ''}
              </small>
            </label>

            <label>
              <span className="field-heading">
                {/* タイトルを持たない既存行の編集では必須にしない (空のまま保存できる) */}
                <span
                  className={isEditing && (template?.title ?? '') === '' ? undefined : 'required'}
                >
                  タイトル
                </span>
                <span className="field-example" data-testid="free-text-template-title-counter">
                  {formatTitleCounter(form.title)}
                </span>
              </span>
              <input
                type="text"
                data-testid="free-text-template-title"
                value={form.title}
                readOnly={saving}
                placeholder="例: 11/7 トンネル工事に伴う車内放送"
                ref={(el) => {
                  fieldRefs.current.title = el;
                }}
                className={fieldErrors.title ? 'is-invalid' : ''}
                aria-invalid={fieldErrors.title ? true : undefined}
                aria-describedby={fieldErrors.title ? 'free-text-template-title-error' : undefined}
                onChange={(e) => updateForm({ title: e.target.value })}
              />
              <FieldError id="free-text-template-title-error" message={fieldErrors.title} />
              <small data-testid="free-text-template-title-help">
                一覧で見分けるための名前です(最大{MAX_TEMPLATE_TITLE_LENGTH}文字・1
                行)。放送内容には影響しません。
                {isEditing && (template?.title ?? '') !== ''
                  ? '設定済みのタイトルを空にすることはできません。'
                  : ''}
              </small>
            </label>

            <label>
              <span className="field-heading">
                <span className="required">テキスト</span>
                <span className="field-example" data-testid="free-text-template-text-counter">
                  {formatTextCounter(form.text)}
                </span>
              </span>
              <textarea
                data-testid="free-text-template-text"
                required
                rows={6}
                value={form.text}
                readOnly={saving}
                placeholder="例: まもなく終点です。お忘れ物にご注意ください。"
                ref={(el) => {
                  fieldRefs.current.text = el;
                }}
                className={fieldErrors.text ? 'is-invalid' : ''}
                aria-invalid={fieldErrors.text ? true : undefined}
                aria-describedby={fieldErrors.text ? 'free-text-template-text-error' : undefined}
                onChange={(e) => updateForm({ text: e.target.value })}
              />
              <FieldError id="free-text-template-text-error" message={fieldErrors.text} />
              {kind === 'synced' ? (
                <small data-testid="free-text-template-text-help">
                  放送地点で流す日本語の文面です(最大
                  {MAX_FREE_TEXT_TEMPLATE_TEXT_LENGTH}文字)。コピーではなく参照されるため、
                  ここを直すと、使っているすべての放送地点の放送が約 1 分以内に自動で変わります。
                </small>
              ) : (
                <small data-testid="free-text-template-text-help">
                  放送地点で流す文面のひな形です(最大
                  {MAX_FREE_TEXT_TEMPLATE_TEXT_LENGTH}文字)。使うときに文面がコピーされるため、
                  ここを直したり消したりしても、登録済みの放送地点は変わりません。
                </small>
              )}
            </label>

            <label>
              <span className="field-heading">
                <span>備考</span>
                <span className="field-example" data-testid="free-text-template-note-counter">
                  {formatNoteCounter(form.note)}
                </span>
              </span>
              <textarea
                data-testid="free-text-template-note"
                rows={3}
                value={form.note}
                readOnly={saving}
                placeholder="例: 根拠となるお知らせ https://example.com/notice"
                ref={(el) => {
                  fieldRefs.current.note = el;
                }}
                className={fieldErrors.note ? 'is-invalid' : ''}
                aria-invalid={fieldErrors.note ? true : undefined}
                aria-describedby={fieldErrors.note ? 'free-text-template-note-error' : undefined}
                onChange={(e) => updateForm({ note: e.target.value })}
              />
              <FieldError id="free-text-template-note-error" message={fieldErrors.note} />
              <small data-testid="free-text-template-note-help">
                任意のメモです(最大{MAX_TEMPLATE_NOTE_LENGTH}文字)。http:// / https:// で始まる URL
                は一覧でリンクになります。放送内容には影響しません。
              </small>
            </label>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => void handlePreview()}
                disabled={previewing || saving || form.text.trim() === ''}
                data-testid="free-text-template-preview"
              >
                {previewing ? '生成中...' : '▶ 試聴'}
              </button>
              {previewUrl ? (
                <audio
                  controls
                  autoPlay
                  src={previewUrl}
                  data-testid="free-text-template-preview-audio"
                />
              ) : null}
            </div>
            {previewError ? (
              <div
                role="alert"
                className="error-message"
                data-testid="free-text-template-preview-error"
              >
                {previewError}
              </div>
            ) : null}
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            data-testid="free-text-template-cancel-edit"
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="primary"
            disabled={saving}
            data-testid="free-text-template-save"
          >
            {saving ? (
              <>
                <Spinner />
                保存中...
              </>
            ) : isEditing ? (
              '更新'
            ) : (
              '登録'
            )}
          </button>
        </div>
      </form>

      {confirmDialog}
    </div>
  );
}
